import { useEffect, useId, useState, type FormEvent } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { usePostHog } from "@posthog/react";
import { X } from "lucide-react";

import { Button } from "../primitives/Button";
import { TextArea } from "../primitives/Field";

const SUPPORT_EMAIL = "support@usesill.com";
const SUCCESS_AUTOCLOSE_MS = 1600;

type Props = {
  open: boolean;
  onClose: () => void;
};

type SubmitState = "idle" | "submitting" | "success" | "error";

type PostHogLike = {
  capture?: (event: string, props?: Record<string, unknown>) => unknown;
};

/**
 * Pro feedback modal — re-skins the consumer feedback flow for the Pro surface
 * while speaking the SAME PostHog survey contract (`survey shown` on open,
 * `survey sent` with `$survey_id` + `$survey_response` on submit). The survey
 * id comes from VITE_POSTHOG_FEEDBACK_SURVEY_ID — point Pro at its own survey.
 *
 * Opened from the header feedback opener (desktop icon + mobile sheet row),
 * which owns the open state — this component is a pure {open, onClose}
 * dialog and holds no trigger of its own.
 */
export function FeedbackModal({ open, onClose }: Props) {
  if (!open) return null;
  return (
    <Dialog.Root open={open} onOpenChange={(next) => (next ? null : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay className="dlg-overlay" />
        <Dialog.Content
          aria-describedby={undefined}
          data-testid="feedback-modal"
          className="dlg-content w-[min(440px,calc(100vw-32px))]"
        >
          {/* Body is its own component so closing unmounts + resets its state. */}
          <FeedbackModalBody onClose={onClose} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function FeedbackModalBody({ onClose }: { onClose: () => void }) {
  const posthog = usePostHog();
  const surveyId = import.meta.env.VITE_POSTHOG_FEEDBACK_SURVEY_ID as
    | string
    | undefined;
  const textareaId = useId();
  const [text, setText] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>("idle");

  // Fire `survey shown` once on mount so PostHog conversion math is accurate.
  useEffect(() => {
    if (!surveyId || !posthog) return;
    try {
      (posthog as unknown as PostHogLike).capture?.call(posthog, "survey shown", {
        $survey_id: surveyId,
      });
    } catch {
      // Non-fatal; capture failures should not block the form.
    }
  }, [posthog, surveyId]);

  // Auto-close after success.
  useEffect(() => {
    if (submitState !== "success") return;
    const id = window.setTimeout(onClose, SUCCESS_AUTOCLOSE_MS);
    return () => window.clearTimeout(id);
  }, [submitState, onClose]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || submitState === "submitting") return;
    if (!surveyId || !posthog) {
      setSubmitState("error");
      return;
    }
    setSubmitState("submitting");
    try {
      (posthog as unknown as PostHogLike).capture?.call(posthog, "survey sent", {
        $survey_id: surveyId,
        $survey_response: trimmed,
      });
      setSubmitState("success");
    } catch (err) {
      console.error("[feedback] capture failed", err);
      setSubmitState("error");
    }
  };

  const submitDisabled =
    !text.trim() || submitState === "submitting" || submitState === "success";

  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div>
          <Dialog.Title className="dlg-title">Send feedback</Dialog.Title>
          <p className="dlg-desc">
            Tell us what's working, what's broken, or what you wish it did.
          </p>
        </div>
        <button
          type="button"
          data-testid="feedback-close"
          aria-label="Close"
          className="-mr-1 -mt-1 rounded-full p-1.5 text-fg-muted outline-none transition-colors hover:bg-black/5 hover:text-fg focus-visible:ring-2 focus-visible:ring-pro-ledge"
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </div>

      {submitState === "success" ? (
        <p
          data-testid="feedback-success"
          className="mt-4 rounded-xl bg-pro-soft px-4 py-3 text-sm text-pro-ink"
        >
          Thanks — we got it.
        </p>
      ) : (
        <form
          data-testid="feedback-form"
          className="mt-4 space-y-3"
          onSubmit={handleSubmit}
        >
          <TextArea
            id={textareaId}
            data-testid="feedback-textarea"
            placeholder="What's on your mind?"
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={submitState === "submitting"}
            aria-label="Feedback"
            rows={5}
          />
          {submitState === "error" && (
            <p className="text-sm text-destructive" data-testid="feedback-error">
              Couldn't send — please email us instead.
            </p>
          )}
          <div className="flex items-center justify-end">
            <Button
              type="submit"
              size="sm"
              disabled={submitDisabled}
              data-testid="feedback-submit"
            >
              {submitState === "submitting" ? "Sending…" : "Send feedback"}
            </Button>
          </div>
        </form>
      )}

      <p className="mt-4 text-xs text-fg-muted">
        Prefer email? Reach us at{" "}
        <a
          className="font-medium text-fg underline underline-offset-2 hover:text-pro-ink"
          href={`mailto:${SUPPORT_EMAIL}`}
        >
          {SUPPORT_EMAIL}
        </a>
        .
      </p>
    </>
  );
}
